import { Tabs, TabsHeader, Tab } from '@material-tailwind/react';
import { Drawer } from '@material-tailwind/react';
import { useEffect, useState } from 'react';
import Buttons from '../../../components/common/Buttons';
import TextWinds from '../../../components/common/TextWinds';
import RegistDetail from './popup/RegistDetail';
import Modify from './popup/Modify';
import StudentList from './StudentList';
import {
  studentStatusType,
  studentType,
} from '../../../constants/types/student-type';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { PaginationProps } from '../../../constants/types';
import { getStudentList } from '../../../api/utils/lms/lmsApi';
import { PAGE_SIZE } from '../../../constants/constants';
import { Button, Tooltip } from 'antd';
import {
  handleCopyClipBoard,
  openNewWindow,
} from '../../../api/utils/util-func';
import {
  DocumentDuplicateIcon,
  QuestionMarkCircleIcon,
} from '@heroicons/react/24/outline';
import { useAppDispatch } from '../../../store/hook';
import { openAlert } from '../../../store/features/modalSlice';

const TABS_MENU = [
  {
    label: '수강생',
    value: studentStatusType.ACTIVE,
  },
  {
    label: '정지',
    value: studentStatusType.INACTIVE,
  },
  {
    label: '대기생',
    value: studentStatusType.PENDING,
  },
];

const Signupmgmt = () => {
  const navigate = useNavigate();
  const { classId } = useParams();
  const dispatch = useAppDispatch();

  // --- SearchParams ---
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab =
    (searchParams.get('status') as studentStatusType) ||
    studentStatusType.PENDING;
  const page = searchParams.get('page') ? +searchParams.get('page') : 1;
  const pageSize = searchParams.get('pageSize')
    ? +searchParams.get('pageSize')
    : PAGE_SIZE;

  // --- 드로어 관리 ---
  const [signupModalOpen, setSignupModalOpen] = useState(false);
  const [modifyModalOpen, setModifyModalOpen] = useState<boolean>(false);

  const [studentList, setStudentList] = useState<studentType[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const [pageInfo, setPageInfo] = useState<PaginationProps>({
    page,
    pageSize,
    lastPage: 0,
    total: 0,
  });

  useEffect(() => {
    if (signupModalOpen || modifyModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'visible';
    }

    return () => {
      document.body.style.overflow = 'visible';
    };
  }, [signupModalOpen, modifyModalOpen]);

  useEffect(() => {
    reloadStudentList();
  }, [activeTab, page, pageSize]);

  const handleCopyClipboard = (text: string) => {
    handleCopyClipBoard(text).then((result) => {
      if (result) {
        dispatch(openAlert({ message: '클래스 코드가 복사되었습니다.' }));
      } else {
        dispatch(openAlert({ message: '클래스 코드 복사에 실패했습니다.' }));
      }
    });
  };

  // 페이지 정보 변경에 따른 학생 리스트 가져오기
  const reloadStudentList = () => {
    setIsLoading(true);

    getStudentList(classId, activeTab, page, pageSize)
      .then((res) => {
        if (page > 1 && res.result.length === 0) {
          searchParams.set('page', res.page.lastPage.toString());
          setSearchParams(searchParams);
          return;
        }

        setStudentList(res.result);
        setPageInfo({
          page,
          pageSize,
          total: res.page.total,
          lastPage: res.page.lastPage,
        });
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <div className="flex flex-col pt-6 xl:px-5">
      {/* 헤더 */}
      <header className="flex flex-col gap-3 px-5 xl:px-0">
        <div className="flex flex-row items-center justify-between">
          <TextWinds type="title_h2" xlType="title_h3">
            학생 관리
          </TextWinds>

          {/* TODO: 페이지별 이용 안내 링크 받으면 주석 제거 예정 */}
          {/* <Button
            type="primary"
            className="shrink-0 rounded-lg !border-purple5 !bg-purple5 px-4 hover:!border-purple6 hover:!bg-purple6"
            onClick={() =>
              openNewWindow(
                'https://jebs-notice.oopy.io/16f768f7-7a39-8042-884c-f4c58f453684',
              )
            }
          >
            이용 안내
          </Button> */}
        </div>

        <div className="mt-2 flex w-full items-center justify-between gap-3 rounded-xl border border-gray3 bg-white px-3 py-2 shadow-sm xl:w-fit xl:min-w-[360px]">
          <div className="flex min-w-0 items-center gap-2">
            <div className="flex shrink-0 items-center gap-1.5">
              <TextWinds type="caption1" color="gray7" className="font-bold">
                클래스 코드
              </TextWinds>

              <Tooltip
                title="‘클래스 코드’는 선생님이 개설한 클래스의 고유 번호로, 학생 계정에 입력해야 소속이 확인됩니다. 클래스가 변경되면 담당 선생님께 코드를 문의해 수정해 주세요."
                placement="bottom"
                zIndex={10002}
              >
                <QuestionMarkCircleIcon className="h-4 w-4 cursor-help text-gray6" />
              </Tooltip>
            </div>
          </div>

          <span className="h-4 w-px shrink-0 bg-gray3" />

          <button
            type="button"
            className="group flex items-center justify-between gap-3 rounded-lg bg-gray0 px-3 py-2 text-left transition-colors hover:bg-blue-50"
            onClick={() => handleCopyClipboard(classId)}
          >
            <TextWinds
              type="content_body3"
              color="gray9"
              className="truncate font-bold tracking-wide group-hover:text-skyblue"
            >
              {classId}
            </TextWinds>

            <div className="flex shrink-0 items-center gap-1 text-gray7 group-hover:text-skyblue">
              <DocumentDuplicateIcon className="h-4 w-4" />

              <TextWinds
                type="caption1"
                className="hidden font-bold group-hover:text-skyblue xl:block"
              >
                복사
              </TextWinds>
            </div>
          </button>
        </div>
      </header>

      <div className="my-5 flex flex-col gap-x-1 bg-white p-4 xl:rounded-lg xl:p-4 xl:pt-6 xl:shadow-card">
        <section className="flex items-center justify-center gap-3 border-b border-gray5 pb-5 xl:justify-end">
          {/* div : 버튼 너비 조정 래퍼 */}
          <div className="w-32">
            <Buttons
              type="filled"
              color="purple5"
              isHeight="titlebutton"
              onClick={() => setSignupModalOpen(true)}
              className="h-10 w-full"
            >
              학생 추가
            </Buttons>
          </div>
          <div className="w-32">
            <Buttons
              type="outlined"
              color="purple5"
              isHeight="titlebutton"
              onClick={() => setModifyModalOpen(true)}
              className="h-10 w-full"
            >
              반 추가/수정
            </Buttons>
          </div>
        </section>

        {/* 탭 */}
        <Tabs value={activeTab} className="w-full">
          <TabsHeader
            className="rounded-none border-b border-gray6 bg-transparent p-0"
            indicatorProps={{
              className:
                'bg-transparent border-b-0 border-purple5 shadow-none rounded-none',
            }}
          >
            {TABS_MENU.map(({ label, value }) => (
              <Tab
                key={value}
                value={value}
                onClick={() => setSearchParams({ status: value })}
                className={`h-[48px] w-[142px] border-b-${
                  activeTab === value ? '2' : '0'
                } border-purple5`}
              >
                <div className="flex">
                  <TextWinds
                    type={`${
                      activeTab === value ? 'button2' : 'content_body3'
                    }`}
                    color={`${activeTab === value ? 'purple5' : 'gray7'}`}
                    xlType={`${
                      activeTab === value ? 'button' : 'content_body2'
                    }`}
                  >
                    {label}
                  </TextWinds>
                </div>
              </Tab>
            ))}
          </TabsHeader>
        </Tabs>

        {/* 중앙 테이블  */}
        <StudentList
          status={activeTab}
          data={studentList}
          pageInfo={pageInfo}
          reloadStudentList={reloadStudentList}
          isLoading={isLoading}
        />

        {/* 학생 등록 모달 */}
        <Drawer
          placement="bottom"
          open={signupModalOpen}
          onClose={() => {
            setSignupModalOpen(false);
          }}
          overlay={true}
          size={608}
          className="left-0 right-1/4 max-h-[100%] w-full items-center overflow-y-auto rounded-t-xl px-5 py-2.5 lg:left-1/4 lg:w-1/2"
        >
          <RegistDetail
            title="학생 등록"
            open={signupModalOpen}
            closeDrawerBottom={() => {
              setSignupModalOpen(false);
            }}
            reloadStudentList={reloadStudentList}
          />
        </Drawer>

        {/* 반 등록 모달 */}
        <Drawer
          placement="bottom"
          open={modifyModalOpen}
          onClose={() => {
            setModifyModalOpen(false);
          }}
          overlay={true}
          size={485}
          className="left-0 right-1/4 max-h-[100%] w-full items-center overflow-y-auto rounded-t-xl px-5 py-4 lg:left-1/4 lg:w-1/2"
        >
          <Modify
            title="반 등록하기"
            closeDrawerBottom={() => {
              setModifyModalOpen(false);
            }}
            open={modifyModalOpen}
          />
        </Drawer>
      </div>
    </div>
  );
};

export default Signupmgmt;
